/**
 * A building's contacts, kept in eMerge (D-30; authority ruling 2026-10-05:
 * facility contacts are EMERGE_OWNED, Monday is history only). One module for
 * every screen that shows or keeps them: the Scheduling facility panel (the
 * board's row and the facility table) and the Business Development account.
 *
 * Every write sends an Idempotency-Key (header and body) made once per form,
 * so a retried click is answered once.
 */
import api from '../../services/api';

export type ContactRole = 'MAIN' | 'BOM' | 'ABOM' | 'DON' | 'SSD' | 'ADMINISTRATOR' | 'MEDICAL_RECORDS' | 'OTHER';
export const CONTACT_ROLE_LABEL: Record<ContactRole, string> = {
  MAIN: 'Main phone', BOM: 'BOM', ABOM: 'ABOM', DON: 'DON', SSD: 'SSD', ADMINISTRATOR: 'Administrator', MEDICAL_RECORDS: 'Medical Records', OTHER: 'Other',
};
/** The roles a building is expected to have someone in, in the order the workforce reads them. */
export const PERSON_ROLES: ReadonlyArray<ContactRole> = ['BOM', 'ABOM', 'DON', 'SSD', 'ADMINISTRATOR', 'MEDICAL_RECORDS'];
export const CARD_ROLES: ReadonlyArray<ContactRole> = [...PERSON_ROLES, 'OTHER'];

export interface ContactLine {
  id: string | null;
  /** LEGACY: a number on a building with no Monday item. Not Monday's, never adoptable; corrected instead. */
  source: 'EMERGE' | 'MONDAY' | 'LEGACY';
  role: ContactRole;
  role_label?: string;
  title: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  notes?: string | null;
  origin_system?: 'EMERGE' | 'MONDAY' | 'LEGACY';
  origin_item_id?: string | null;
  authority?: 'EMERGE';
  /** A Monday line eMerge has not taken over yet. */
  adoptable?: boolean;
  effective_from?: string | null;
  recorded_by_name?: string | null;
  reason?: string | null;
  imported_at?: string | null;
}
export interface MainPhoneLine extends ContactLine { monday_phone?: string | null }
export interface ContactDirectory {
  facility_id: string;
  main_phone: MainPhoneLine | null;
  contacts: ContactLine[];
  by_role?: Partial<Record<ContactRole, ContactLine[]>>;
  missing_roles: ContactRole[];
  adoptable?: number;
  /** The call-sheet view a reader without facility.contact.write gets: notes, reasons and authors are left out. */
  summary?: boolean;
}
export type ContactEventType = 'ADDED' | 'EDITED' | 'REPLACED' | 'ENDED' | 'ADOPTED' | 'MAIN_PHONE_SET' | 'MAIN_PHONE_ENDED';
export interface ContactHistoryEntry {
  id: string; event_type: ContactEventType; role: ContactRole | null;
  before: Partial<ContactLine> | null; after: Partial<ContactLine> | null; reason: string; recorded_at: string; actor_name: string | null;
}
export interface ContactsRead { directory: ContactDirectory; history: ContactHistoryEntry[]; can: { write: boolean } }
export interface ApiResult<T> { success: boolean; data?: T; error?: string; code?: string; message?: string; detail?: unknown; status?: number }

export type ContactMode = 'ADD' | 'EDIT' | 'REPLACE';
export interface ContactWrite {
  role: ContactRole; mode?: ContactMode; name?: string | null; title?: string | null; phone?: string | null; email?: string | null;
  notes?: string | null; reason: string; replaces_contact_id?: string | null;
}

export const freshKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const keyed = (key: string) => ({ headers: { 'Idempotency-Key': key } });

export const facilityContactsApi = {
  read: async (facilityId: string): Promise<ApiResult<ContactsRead>> =>
    (await api.get(`/facilities/${facilityId}/contacts`)) as ApiResult<ContactsRead>,
  record: async (facilityId: string, body: ContactWrite, key: string): Promise<ApiResult<{ record: { id: string }; replayed: boolean; directory: ContactDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts`, { ...body, idempotency_key: key }, keyed(key))) as ApiResult<{ record: { id: string }; replayed: boolean; directory: ContactDirectory }>,
  end: async (facilityId: string, contactId: string, reason: string, key: string): Promise<ApiResult<{ ended: string; directory: ContactDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts/${contactId}/end`, { reason, idempotency_key: key }, keyed(key))) as ApiResult<{ ended: string; directory: ContactDirectory }>,
  /** One Monday line as it stood; `contactId` is the building's own id for Monday's main number. */
  adopt: async (facilityId: string, contactId: string, key: string): Promise<ApiResult<{ record: { id: string }; directory: ContactDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts/${contactId}/adopt`, { idempotency_key: key }, keyed(key))) as ApiResult<{ record: { id: string }; directory: ContactDirectory }>,
  adoptAll: async (facilityId: string, key: string): Promise<ApiResult<{ adopted: { id: string; from: string }[]; needs_edit: { id: string; role: ContactRole; code: string }[]; replayed?: boolean; directory: ContactDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts/adopt-all`, { idempotency_key: key }, keyed(key))) as ApiResult<{ adopted: { id: string; from: string }[]; needs_edit: { id: string; role: ContactRole; code: string }[]; replayed?: boolean; directory: ContactDirectory }>,
};

/** Refusals that mean "someone changed this building first": the section re-reads what is current. */
export const STALE_CODES: ReadonlySet<string> = new Set(['STALE', 'ALREADY_ENDED', 'ROLE_HAS_CONTACT', 'ROLE_MISMATCH']);

/**
 * Said when the server did not answer. The request may have been recorded
 * before the connection dropped, so it never claims nothing changed; every
 * write keeps its Idempotency-Key until a definitive answer, so a retry is
 * answered once.
 */
export const NO_ANSWER = 'Could not confirm this with the server. Try again: a retry will not record it twice.';

/**
 * A server failure on a write. A contact write commits before its directory
 * read, so a 5xx (or a gateway timeout, 408) can follow a saved change: it
 * never claims nothing was saved. The form or adopt keeps its key, so the
 * retry is the same request and is recorded once.
 */
/**
 * A form's own key came back KEY_REUSED: only this form's earlier submit can
 * have used it, so that attempt was recorded (its answer was lost, or was a
 * 5xx after the commit). The form reloads the contacts and takes a fresh key,
 * so the next submit is judged as a new change against what is current.
 */
export const EARLIER_SAVED = 'Your earlier try was saved, so this was not recorded again. The contacts are reloaded: check them before you change anything again.';

export const WRITE_FAILED = 'The server did not confirm this, so it may have been saved. Try again: a retry will not record it twice.';

/**
 * A refusal in words the reader can act on. Only a definitive refusal (a 4xx
 * other than 408) says nothing was saved. `read` is a failed read of the
 * directory: it says nothing about saving at all (Copilot review of #349).
 */
export function refusalText(r: ApiResult<unknown>, opts: { read?: boolean } = {}): string {
  if (opts.read) {
    if (r.status === undefined) return 'Could not reach the server.';
    if (r.status >= 500) return 'The server failed while answering. It is not a permissions problem.';
    return r.message || r.error || 'The server refused the request.';
  }
  if (r.status === undefined) return NO_ANSWER;
  if (r.status >= 500 || r.status === 408) return WRITE_FAILED;
  return r.message || r.error || 'That did not go through. Nothing was saved.';
}
